import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import DataTable from "react-data-table-component";
import { Card, Button, Form, Row, Col, Spinner } from "react-bootstrap";
import * as XLSX from "xlsx";
import { FaFileExcel, FaArrowLeft } from "react-icons/fa";
import Topbar from "../components/Topbar";
import Sidebar from "../components/Sidebar";

// Chairman's Layout Components

function TradeLicenseHistoryChairman() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [licenseDetails, setLicenseDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // API and Token for Chairman
  const API_URL = process.env.REACT_APP_SUPER_ADMIN_API_URL;
  const token = localStorage.getItem("adminUser")
    ? JSON.parse(localStorage.getItem("adminUser")).token
    : null;

  useEffect(() => {
    const fetchHistory = async () => {
      setLoading(true);
      try {
        // Fetch payment history
        const historyResponse = await axios.get(
          `${API_URL}list-payment-history-by tradeid.php?trade_license_id=${id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (
          historyResponse.data.success &&
          Array.isArray(historyResponse.data.data)
        ) {
          setHistory(historyResponse.data.data);
          // Set license details from the first record of history if available
          if (historyResponse.data.data.length > 0) {
            setLicenseDetails({
              trade_name: historyResponse.data.data[0].trade_name,
              trade_owner: historyResponse.data.data[0].trade_owner,
            });
          }
        } else {
          setHistory([]);
        }

        // Fetch details separately in case history is empty but license exists
        if (!licenseDetails) {
          const licenseResponse = await axios.get(
            `${API_URL}/get-trade-licence.php?id=${id}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          if (licenseResponse.data.success) {
            setLicenseDetails(licenseResponse.data.data);
          }
        }
      } catch (error) {
        toast.error("Failed to fetch payment history.");
        console.error("API Error:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, [id, API_URL, token]);

  const filteredHistory = useMemo(() => {
    if (!startDate || !endDate) {
      return history;
    }
    return history.filter((item) => {
      const paymentDate = item.reg_date; // API response uses 'reg_date' for payment
      return paymentDate >= startDate && paymentDate <= endDate;
    });
  }, [history, startDate, endDate]);

  const handleExport = () => {
    const dataToExport = filteredHistory.map((item) => ({
      "Bill No": item.bill_no || "N/A",
      "Payment Type": item.type,
      Amount: item.amount,
      "Payment Date": item.reg_date,
      "Validity Period": item.validity_peroid,
      "Expire Date": item.expire_date,
    }));
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "PaymentHistory");
    XLSX.writeFile(
      workbook,
      `TradeLicense_History_${licenseDetails?.trade_name}.xlsx`
    );
  };

  const columns = [
    {
      name: "Bill No.",
      selector: (row) => row.bill_no || "N/A",
      sortable: true,
    },
    {
      name: "Payment Type",
      selector: (row) => row.type,
      sortable: true,
      cell: (row) => (
        <span
          className={`text-capitalize badge bg-${
            row.type === "register" ? "primary" : "success"
          }`}
        >
          {row.type}
        </span>
      ),
    },
    {
      name: "Amount (₹)",
      selector: (row) => parseFloat(row.amount).toLocaleString("en-IN"),
      sortable: true,
    },
    {
      name: "Payment Date",
      selector: (row) => row.reg_date,
      sortable: true,
    },
    {
      name: "Validity",
      selector: (row) => row.validity_peroid,
      sortable: true,
    },
    { name: "Expire Date", selector: (row) => row.expire_date, sortable: true },
  ];

  return (
    <>
      <Topbar />
      <div className="d-flex" style={{ minHeight: "calc(100vh - 56px)" }}>
        <Sidebar />
        <div className="flex-grow-1 p-3" style={{ background: "#f0f2f5" }}>
          <Card
            className="shadow-sm"
            style={{ border: "none", borderRadius: "18px" }}
          >
            <Card.Header
              as="h5"
              style={{ background: "#fff", padding: "20px" }}
            >
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                  <Button
                    variant="link"
                    onClick={() => navigate(-1)}
                    className="p-0 me-3"
                    title="Go Back"
                  >
                    <FaArrowLeft />
                  </Button>
                  <div>
                    Payment History:
                    <span className="fw-bold ms-2">
                      {licenseDetails
                        ? `${licenseDetails.trade_name} (${licenseDetails.trade_owner})`
                        : "Loading..."}
                    </span>
                  </div>
                </div>
              </div>
            </Card.Header>
            <Card.Body>
              <Form className="p-3 border-bottom">
                <Row className="g-3 align-items-end">
                  <Col md={4}>
                    <Form.Label>Start Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Label>End Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </Col>
                  <Col md={4} className="text-md-end">
                    <Button onClick={handleExport} variant="success" size="sm">
                      <FaFileExcel className="me-2" />
                      Export History
                    </Button>
                  </Col>
                </Row>
              </Form>
              <DataTable
                columns={columns}
                data={filteredHistory}
                pagination
                responsive
                highlightOnHover
                progressPending={loading}
                progressComponent={
                  <div className="p-5 text-center">
                    <Spinner animation="border" />
                  </div>
                }
              />
            </Card.Body>
          </Card>
        </div>
      </div>
    </>
  );
}

export default TradeLicenseHistoryChairman;
